import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { UserLocation, LocationPreferences, UIPreferences } from '../types';
import { logger } from '../utils/logger';
import { AppDispatch } from './index';

interface UserState {
  locationPreferences: LocationPreferences;
  uiPreferences: UIPreferences;
  isLoadingLocation: boolean;
  locationError: string | null;
}

const initialState: UserState = {
  locationPreferences: {
    useCurrentLocation: true,
    savedLocation: undefined,
    autoDetectLocation: true,
  },
  uiPreferences: {
    showZoomButtons: true,
  },
  isLoadingLocation: false,
  locationError: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLocationPreferences: (state, action: PayloadAction<LocationPreferences>) => {
      state.locationPreferences = action.payload;
    },
    setSavedLocation: (state, action: PayloadAction<UserLocation>) => {
      state.locationPreferences.savedLocation = action.payload;
      state.locationPreferences.useCurrentLocation = false;
    },
    setUseCurrentLocation: (state, action: PayloadAction<boolean>) => {
      state.locationPreferences.useCurrentLocation = action.payload;
    },
    setAutoDetectLocation: (state, action: PayloadAction<boolean>) => {
      state.locationPreferences.autoDetectLocation = action.payload;
    },
    setLocationLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoadingLocation = action.payload;
    },
    setLocationError: (state, action: PayloadAction<string | null>) => {
      state.locationError = action.payload;
    },
    clearSavedLocation: (state) => {
      state.locationPreferences.savedLocation = undefined;
      state.locationPreferences.useCurrentLocation = true;
    },
    setUIPreferences: (state, action: PayloadAction<UIPreferences>) => {
      state.uiPreferences = action.payload;
    },
    setShowZoomButtons: (state, action: PayloadAction<boolean>) => {
      state.uiPreferences.showZoomButtons = action.payload;
    },
  },
});

export const {
  setLocationPreferences,
  setSavedLocation,
  setUseCurrentLocation,
  setAutoDetectLocation,
  setLocationLoading,
  setLocationError,
  clearSavedLocation,
  setUIPreferences,
  setShowZoomButtons,
} = userSlice.actions;

export const saveLocationPreferences = (preferences: LocationPreferences) => async (dispatch: AppDispatch): Promise<void> => {
  try {
    await AsyncStorage.setItem('locationPreferences', JSON.stringify(preferences));
    dispatch(setLocationPreferences(preferences));
  } catch (error) {
    logger.error(`Error saving location preferences: ${error instanceof Error ? error.message : String(error)}`);
    dispatch(setLocationError('Failed to save location preferences'));
  }
};

export const loadLocationPreferences = () => async (dispatch: AppDispatch): Promise<void> => {
  try {
    dispatch(setLocationLoading(true));
    const savedPreferences = await AsyncStorage.getItem('locationPreferences');
    if (savedPreferences !== null && savedPreferences !== '') {
      const preferences = JSON.parse(savedPreferences) as LocationPreferences;
      dispatch(setLocationPreferences(preferences));
    }
  } catch (error) {
    logger.error(`Error loading location preferences: ${error instanceof Error ? error.message : String(error)}`);
    dispatch(setLocationError('Failed to load location preferences'));
  } finally {
    dispatch(setLocationLoading(false));
  }
};

export const saveUIPreferences = (preferences: UIPreferences) => async (dispatch: AppDispatch): Promise<void> => {
  try {
    await AsyncStorage.setItem('uiPreferences', JSON.stringify(preferences));
    dispatch(setUIPreferences(preferences));
  } catch (error) {
    logger.error(`Error saving UI preferences: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export const loadUIPreferences = () => async (dispatch: AppDispatch): Promise<void> => {
  try {
    const savedPreferences = await AsyncStorage.getItem('uiPreferences');
    if (savedPreferences !== null && savedPreferences !== '') {
      const preferences = JSON.parse(savedPreferences) as UIPreferences;
      dispatch(setUIPreferences(preferences));
    }
  } catch (error) {
    logger.error(`Error loading UI preferences: ${error instanceof Error ? error.message : String(error)}`);
  }
};

export default userSlice.reducer;